import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AccountTypeSelector from '../components/AccountTypeSelector';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, login } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    accountType: 'individual',
    schoolName: '',
    districtName: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleData, setGoogleData] = useState(null);
  const [showAccountTypeSelector, setShowAccountTypeSelector] = useState(false);
  const [message, setMessage] = useState(null);
  const [showSchoolFields, setShowSchoolFields] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (formData.accountType === 'school_admin' && !formData.schoolName) {
      setError('School name is required for school administrators');
      setIsLoading(false);
      return;
    }

    try {
      const registerData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        accountType: formData.accountType,
        ...(formData.accountType === 'school_admin' && {
          schoolName: formData.schoolName.trim(),
          districtName: formData.districtName?.trim() || undefined
        })
      };

      console.log('Submitting registration data:', {
        ...registerData,
        password: '[REDACTED]'
      });

      const registerResponse = await register(registerData);

      if (registerResponse.success) {
        setMessage({
          type: 'success',
          text: formData.accountType === 'school_admin'
            ? 'School administrator registration successful!'
            : 'Registration successful! You have been given 3 credits to start.'
        });
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(registerResponse.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'accountType') {
      setShowSchoolFields(value === 'school_admin');
    }
  };

  const handleGoogleLogin = (e) => {
    e.preventDefault();
    
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const authUrl = `${API_URL}/api/auth/google`;
    console.log('Google auth URL:', authUrl);
    
    window.open(
      authUrl,
      'Google Sign In',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  const handleAccountTypeSelect = async (selectedType) => {
    try {
      setIsLoading(true);
      setError('');

      if (!googleData) {
        setError('No Google data available');
        return;
      }

      const registrationData = {
        firstName: googleData.firstName || googleData.given_name,
        lastName: googleData.lastName || googleData.family_name,
        email: googleData.email,
        accountType: selectedType.toLowerCase(),
        googleId: googleData.sub || googleData.id,
        profilePicture: googleData.picture
      };

      console.log('Completing Google registration with:', registrationData);

      const registerResponse = await register(registrationData);

      if (registerResponse.success) {
        console.log('Google registration successful:', registerResponse);
        setShowAccountTypeSelector(false);
        navigate('/dashboard');
      } else {
        setError(registerResponse.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Google registration error:', error);
      setError(
        error.response?.data?.message || 
        'Failed to complete registration. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== API_URL) {
        return;
      }

      console.log('Received message:', event.data);

      if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
        const { isNewUser, userData, token } = event.data;
        
        if (isNewUser) {
          setGoogleData(userData);
          setShowAccountTypeSelector(true);
        } else {
          login(userData);
          navigate('/dashboard');
        }
      } else if (event.data.type === 'GOOGLE_AUTH_FAILURE') {
        console.error('Google auth failed:', event.data.error);
        setError('Google authentication failed. Please try again.');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [login, navigate, API_URL]);

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Register
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Select
                  fullWidth
                  label="Account Type"
                  name="accountType"
                  value={formData.accountType}
                  onChange={handleChange}
                >
                  <MenuItem value="individual">Individual</MenuItem>
                  <MenuItem value="corporate">Corporate</MenuItem>
                  <MenuItem value="school_admin">School Administrator</MenuItem>
                </Select>
              </Grid>
              {formData.accountType === 'school_admin' && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="School Name"
                      name="schoolName"
                      value={formData.schoolName}
                      onChange={handleChange}
                      required
                      error={formData.accountType === 'school_admin' && !formData.schoolName}
                      helperText={
                        formData.accountType === 'school_admin' && !formData.schoolName
                          ? "School name is required"
                          : "Enter your school's name"
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="School District Name (Optional)"
                      name="districtName"
                      value={formData.districtName}
                      onChange={handleChange}
                      helperText="Enter your school district's name (optional)"
                    />
                  </Grid>
                </>
              )}
            </Grid>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isLoading}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? 'Registering...' : 'Register'}
            </Button>
          </form>

          {/* Google Sign Up - Temporarily disabled
          <Divider sx={{ my: 2 }}>OR</Divider>

          <Button
            onClick={handleGoogleLogin}
            variant="contained"
            startIcon={<GoogleIcon />}
            fullWidth
            sx={{ mt: 2, mb: 2 }}
          >
            Sign up with Google
          </Button>
          */}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Already have an account? <Link to="/login">Login</Link>
            </Typography>
          </Box>
        </Paper>
      </Box>

      <AccountTypeSelector
        open={showAccountTypeSelector}
        onSelect={handleAccountTypeSelect}
        onClose={() => setShowAccountTypeSelector(false)}
        googleData={googleData}
      />
    </Container>
  );
};

export default RegisterPage;